import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import DesktopLayout from '../../../components/layout/DesktopLayout';
import * as adminOrdersApi from '../../../services/adminOrdersApi';
import type { AdminOrderDetail } from '../../../services/adminOrdersApi';
import './AdminOrdersPage.css';

const STATUS_OPTIONS = [
  { value: 'pending', label: '待處理', color: '#ff9800' },
  { value: 'processing', label: '處理中', color: '#2196f3' },
  { value: 'shipped', label: '已出貨', color: '#9c27b0' },
  { value: 'delivered', label: '已送達', color: '#4caf50' },
  { value: 'cancelled', label: '已取消', color: '#f44336' },
  { value: 'failed', label: '失敗', color: '#f44336' },
];

export default function AdminOrderDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  const [order, setOrder] = useState<AdminOrderDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Status update form
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');

  // Shipping update form
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');
  const [shippingNote, setShippingNote] = useState('');

  const fetchOrder = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await adminOrdersApi.getAdminOrderById(
        id,
        localStorage.getItem('auth_token') || ''
      );

      setOrder(response.data);
      setNewStatus(response.data.status);
      setTrackingNumber(response.data.shipping?.trackingNumber || '');
      setCarrier(response.data.shipping?.carrier || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入訂單失敗');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  // Handle status update
  const handleStatusUpdate = async () => {
    if (!newStatus || !id) return;

    setIsUpdating(true);
    setError(null);

    try {
      await adminOrdersApi.updateOrderStatus(
        id,
        localStorage.getItem('auth_token') || '',
        newStatus,
        statusNote || undefined
      );

      await fetchOrder();
      setStatusNote('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新狀態失敗');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle shipping update
  const handleShippingUpdate = async () => {
    if (!id) return;

    setIsUpdating(true);
    setError(null);

    try {
      await adminOrdersApi.updateOrderShipping(
        id,
        {
          status: order?.status === 'pending' ? 'processing' : 'shipped',
          trackingNumber: trackingNumber || undefined,
          carrier: carrier || undefined,
          note: shippingNote || undefined,
        },
        localStorage.getItem('auth_token') || ''
      );

      await fetchOrder();
      setShippingNote('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新出貨資訊失敗');
    } finally {
      setIsUpdating(false);
    }
  };

  // Format price
  const formatPrice = (price: number) => {
    return `$${Math.round(price).toLocaleString()}`;
  };

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
    return (
      <DesktopLayout>
        <div className="admin-orders-page">
          <div className="admin-orders-page__error">
            您沒有權限訪問此頁面
          </div>
        </div>
      </DesktopLayout>
    );
  }

  if (isLoading) {
    return (
      <DesktopLayout>
        <div className="admin-orders-page">
          <div className="admin-orders-page__loading">載入中...</div>
        </div>
      </DesktopLayout>
    );
  }

  if (!order) {
    return (
      <DesktopLayout>
        <div className="admin-orders-page">
          <div className="admin-orders-page__empty">
            <p>找不到訂單</p>
            <button onClick={() => navigate('/admin/orders')} className="admin-orders-page__back-button">
              返回列表
            </button>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout>
      <div className="admin-orders-page">
        {/* Header */}
        <div className="admin-order-detail__header">
          <button onClick={() => navigate('/admin/orders')} className="admin-order-detail__back-button">
            ← 返回列表
          </button>
          <h1>訂單詳情</h1>
        </div>

        {/* Error */}
        {error && (
          <div className="admin-orders-page__error">
            {error}
            <button onClick={() => setError(null)} className="admin-orders-page__error-close">×</button>
          </div>
        )}

        {/* Order Info Cards */}
        <div className="admin-order-detail__grid">
          {/* Basic Info */}
          <div className="admin-order-detail__card">
            <h2 className="admin-order-detail__card-title">訂單資訊</h2>
            <div className="admin-order-detail__card-content">
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">訂單編號</span>
                <span className="admin-order-detail__value">{order.orderNumber}</span>
              </div>
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">訂單狀態</span>
                <span
                  className="admin-order-detail__status-badge"
                  style={{ backgroundColor: STATUS_OPTIONS.find(s => s.value === order.status)?.color || '#999' }}
                >
                  {STATUS_OPTIONS.find(s => s.value === order.status)?.label || order.status}
                </span>
              </div>
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">建立時間</span>
                <span className="admin-order-detail__value">{formatDate(order.createdAt)}</span>
              </div>
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">更新時間</span>
                <span className="admin-order-detail__value">{formatDate(order.updatedAt)}</span>
              </div>
            </div>
          </div>

          {/* Customer Info */}
          <div className="admin-order-detail__card">
            <h2 className="admin-order-detail__card-title">客戶資訊</h2>
            <div className="admin-order-detail__card-content">
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">姓名</span>
                <span className="admin-order-detail__value">{order.customer.name}</span>
              </div>
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">Email</span>
                <span className="admin-order-detail__value">{order.customer.email}</span>
              </div>
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">電話</span>
                <span className="admin-order-detail__value">{order.customer.phone || '-'}</span>
              </div>
            </div>
          </div>

          {/* Recipient Info */}
          <div className="admin-order-detail__card">
            <h2 className="admin-order-detail__card-title">收件人資訊</h2>
            <div className="admin-order-detail__card-content">
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">姓名</span>
                <span className="admin-order-detail__value">{order.recipient.name}</span>
              </div>
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">電話</span>
                <span className="admin-order-detail__value">{order.recipient.phone}</span>
              </div>
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">地址</span>
                <span className="admin-order-detail__value">{order.recipient.address}</span>
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div className="admin-order-detail__card">
            <h2 className="admin-order-detail__card-title">付款資訊</h2>
            <div className="admin-order-detail__card-content">
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">付款方式</span>
                <span className="admin-order-detail__value">
                  {order.payment.method || '-'}
                </span>
              </div>
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">付款狀態</span>
                <span className="admin-order-detail__value">
                  {order.payment.status === 'paid' ? '已付款' : '未付款'}
                </span>
              </div>
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">付款時間</span>
                <span className="admin-order-detail__value">{formatDate(order.payment.paidAt)}</span>
              </div>
            </div>
          </div>

          {/* Shipping Info */}
          <div className="admin-order-detail__card">
            <h2 className="admin-order-detail__card-title">出貨資訊</h2>
            <div className="admin-order-detail__card-content">
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">出貨狀態</span>
                <span className="admin-order-detail__value">
                  {order.shipping?.status === 'shipped' ? '已出貨' : '未出貨'}
                </span>
              </div>
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">物流公司</span>
                <span className="admin-order-detail__value">{order.shipping?.carrier || '-'}</span>
              </div>
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">追蹤號碼</span>
                <span className="admin-order-detail__value">{order.shipping?.trackingNumber || '-'}</span>
              </div>
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">出貨時間</span>
                <span className="admin-order-detail__value">{formatDate(order.shipping?.shippedAt)}</span>
              </div>
            </div>
          </div>

          {/* Amount Breakdown */}
          <div className="admin-order-detail__card">
            <h2 className="admin-order-detail__card-title">金額明細</h2>
            <div className="admin-order-detail__card-content">
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">小計</span>
                <span className="admin-order-detail__value">{formatPrice(order.subtotal)}</span>
              </div>
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">運費</span>
                <span className="admin-order-detail__value">{formatPrice(order.shippingFee)}</span>
              </div>
              <div className="admin-order-detail__row">
                <span className="admin-order-detail__label">折扣</span>
                <span className="admin-order-detail__value">-{formatPrice(order.discount)}</span>
              </div>
              <div className="admin-order-detail__row admin-order-detail__row--total">
                <span className="admin-order-detail__label">總計</span>
                <span className="admin-order-detail__value">{formatPrice(order.total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div className="admin-order-detail__card admin-order-detail__card--full">
          <h2 className="admin-order-detail__card-title">訂單項目</h2>
          <div className="admin-order-detail__items">
            {order.items.map((item) => (
              <div key={item.id} className="admin-order-detail__item">
                {item.productSnapshot.featuredImageUrl && (
                  <img
                    src={item.productSnapshot.featuredImageUrl}
                    alt={item.productSnapshot.title}
                    className="admin-order-detail__item-image"
                  />
                )}
                <div className="admin-order-detail__item-details">
                  <div className="admin-order-detail__item-name">{item.productSnapshot.title}</div>
                  {item.variantAttributes && Object.keys(item.variantAttributes).length > 0 && (
                    <div className="admin-order-detail__item-variants">
                      {Object.entries(item.variantAttributes).map(([key, value]) => (
                        <span key={key} className="admin-order-detail__item-variant">
                          {key}: {value}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="admin-order-detail__item-qty-price">
                    <span>數量：{item.quantity}</span>
                    <span>單價：{formatPrice(item.price)}</span>
                    <span className="admin-order-detail__item-subtotal">小計：{formatPrice(item.subtotal)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="admin-order-detail__actions">
          {/* Status Update */}
          <div className="admin-order-detail__action-section">
            <h3 className="admin-order-detail__action-title">更新訂單狀態</h3>
            <div className="admin-order-detail__action-form">
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="admin-order-detail__select"
              >
                {STATUS_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="備註（選填）"
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                className="admin-order-detail__input"
              />
              <button
                onClick={handleStatusUpdate}
                disabled={isUpdating || newStatus === order.status}
                className="admin-order-detail__button"
              >
                {isUpdating ? '更新中...' : '更新狀態'}
              </button>
            </div>
          </div>

          {/* Shipping Update */}
          <div className="admin-order-detail__action-section">
            <h3 className="admin-order-detail__action-title">更新出貨資訊</h3>
            <div className="admin-order-detail__action-form">
              <input
                type="text"
                placeholder="物流公司"
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                className="admin-order-detail__input"
              />
              <input
                type="text"
                placeholder="追蹤號碼"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                className="admin-order-detail__input"
              />
              <input
                type="text"
                placeholder="備註（選填）"
                value={shippingNote}
                onChange={(e) => setShippingNote(e.target.value)}
                className="admin-order-detail__input"
              />
              <button
                onClick={handleShippingUpdate}
                disabled={isUpdating}
                className="admin-order-detail__button"
              >
                {isUpdating ? '更新中...' : '更新出貨'}
              </button>
            </div>
          </div>
        </div>

        {/* Notes */}
        {order.notes && (
          <div className="admin-order-detail__card admin-order-detail__card--full">
            <h2 className="admin-order-detail__card-title">訂單備註</h2>
            <div className="admin-order-detail__card-content">
              <p className="admin-order-detail__notes">{order.notes}</p>
            </div>
          </div>
        )}
      </div>
    </DesktopLayout>
  );
}
